/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Call,
  CallDirection,
  CallDisconnectStatus,
  CallStatus,
  SdpPayload,
  WACallTerminateCustomPayload,
} from '../typings';
import {
  declineCallApi,
  answerIncommingCallApi,
  keepAlivePingApi,
  createOutgoingCallApi,
  getNewReconnectSDPApi,
} from '../request';
import useConversationNavigate from '../../../shared/hooks/use-conversation-navigate';
import useNotifications from '../../../shared/hooks/use-notifications';
import useCallRingtone from '../../../shared/hooks/useCallRingtone';
import useOutgoingCallRingtone from '../../../shared/hooks/useOutgoingCallRingtone';
import { useInfinitePolling } from '../../../shared/hooks/use-infinite-poll';
import { CALL_PING_POLL_MAX_RETRIES, CALL_PING_POLL_RETRY_TIME_MS, CALL_PING_POLL_TIME_MS } from '../constants';
import ChipView from './ChipView';
import ExpandedView from './ExpandedView';
import { useCallOperations } from '../hooks/use-call-operations';
import { selectedChatAtom } from '../../home/v1/states/selected-chat';
import { useAtomValue } from 'jotai';

export interface CallChipProps {
  call: Call & { callStartTime?: number };
  callIndex?: number;
  isActive: boolean;
  onSelect: (call: Call) => void;
  onAcceptStateChange: (call: Call, state: CallStatus) => void;
  onRequestAccept: (call: Call, switchCall?: boolean) => void;
  onDisconnect: (status: CallDisconnectStatus, call: Call, removeWithoutDelay?: boolean) => void;
  onCloseChip: (call: Call) => void;
  onMinimize?: (call: Call) => void;
  shouldPlayRingtone?: boolean;
  isAnyCallActive?: boolean;
}

export const CallChip: React.FC<CallChipProps> = ({
  call,
  isActive,
  onSelect,
  onAcceptStateChange,
  onRequestAccept,
  onDisconnect,
  onCloseChip,
  onMinimize,
  shouldPlayRingtone = false,
  callIndex = 0,
  isAnyCallActive = false,
}) => {
  // WebRTC state (copied from previous CallPopup)
  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOff, setIsSpeakerOff] = useState(false);
  const [disconnectReason, setDisconnectReason] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const localAudioRef = useRef<HTMLAudioElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const didStartRingingRef = useRef(false);
  const isSettingUpWebRTCRef = useRef(false);
  const outgoingStartedRef = useRef(false);

  const selectedChat = useAtomValue(selectedChatAtom);

  const { navigateToConversation } = useConversationNavigate();
  const { addNotification } = useNotifications();
  const { startRingtone, stopRingtone } = useCallRingtone();
  const { stopOutgoingRingtone } = useOutgoingCallRingtone({
    direction: call.direction,
    status: call.status,
  });
  const { updateOutgoingCallId } = useCallOperations();

  // Stop ringtone when minimizing
  useEffect(() => {
    if (!isActive && didStartRingingRef.current) {
      stopRingtone();
      didStartRingingRef.current = false;
    }
  }, [isActive, stopRingtone]);

  const keepAliveApiCall = useMemo(() => {
    return async () => {
      try {
        const result = await keepAlivePingApi({
          callId: call.callId,
        });
        return result;
      } catch (error) {
        console.error('Keep-alive ping failed for call:', call.callId, error);
        throw error;
      }
    };
  }, [call.callId]);

  useInfinitePolling(keepAliveApiCall, {
    interval: CALL_PING_POLL_TIME_MS,
    maxRetries: CALL_PING_POLL_MAX_RETRIES,
    retryDelay: CALL_PING_POLL_RETRY_TIME_MS,
    enabled: call.status === CallStatus.IN_PROGRESS,
    dependencies: [call.status],
  });

  // Warn before unload if this call is active/connecting/connected
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (
        call.status === CallStatus.IN_PROGRESS ||
        call.status === CallStatus.Connecting ||
        call.status === CallStatus.Ringing
      ) {
        e.preventDefault();
        e.returnValue = 'You have an active call. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [call.status]);

  // Force disconnect if requested or cleanup local media for switch calls
  useEffect(() => {
    if (
      call.shouldForceDisconnect &&
      (call.status === CallStatus.IN_PROGRESS ||
        call.status === CallStatus.Connecting ||
        call.status === CallStatus.Ringing)
    ) {
      void handleDisconnect(CallStatus.END, 'Call ended due to switching');
    }

    // Clean up local media when call is INITIATED and switchCall is true
    if (call.status === CallStatus.INITIATED && call.switchCall) {
      cleanUpLocalMedia();
    }
  }, [call.shouldForceDisconnect, call.status, call.switchCall]);

  // Auto answer if requested (set by parent)
  useEffect(() => {
    if (call.direction === CallDirection.INCOMING && call.shouldConnect && call.status === CallStatus.INITIATED) {
      void acceptCall();
    }
  }, [call.shouldConnect]);

  const servers = useMemo(
    () => ({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }],
    }),
    []
  );

  const cleanUpLocalMedia = () => {
    if (localAudioRef.current?.srcObject instanceof MediaStream) {
      const stream = localAudioRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => {
        track.stop();
        stream.removeTrack(track);
      });
      localAudioRef.current.srcObject = null;
    }
    if (localStream) {
      localStream.getTracks().forEach((track) => {
        track.stop();
        localStream.removeTrack(track);
      });
      setLocalStream(null);
    }
  };

  const cleanUpRemoteMedia = () => {
    if (remoteAudioRef.current?.srcObject instanceof MediaStream) {
      const stream = remoteAudioRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => {
        track.stop();
        stream.removeTrack(track);
      });
      remoteAudioRef.current.srcObject = null;
    }
    if (remoteStream) {
      remoteStream.getTracks().forEach((track) => {
        track.stop();
        remoteStream.removeTrack(track);
      });
      setRemoteStream(null);
    }
  };
  const cleanUp = () => {
    stopRingtone();
    stopOutgoingRingtone();
    isSettingUpWebRTCRef.current = false;

    cleanUpLocalMedia();
    cleanUpRemoteMedia();

    if (peerConnection) {
      peerConnection.close();
      setPeerConnection(null);
    }
  };

  useEffect(() => {
    if (
      call.direction === CallDirection.INCOMING &&
      call.status === CallStatus.INITIATED &&
      shouldPlayRingtone &&
      !call.switchCall
    ) {
      startRingtone();
      didStartRingingRef.current = true;
    }

    return () => {
      if (didStartRingingRef.current) {
        stopRingtone();
        didStartRingingRef.current = false;
      }
    };
  }, [call.status, shouldPlayRingtone, call.switchCall]);

  // Socket event for call disconnection for this call
  useEffect(() => {
    const handleCallDisconnected = async (event: MessageEvent) => {
      const payload = event.data.payload as WACallTerminateCustomPayload;
      if (event.data.type === 'CALL_TERMINATE' && payload.callId === call.callId) {
        if (call.status !== CallStatus.END) {
          // TODO: decide for END or MISSED based on current status on call
          let disconnectStatus = CallStatus.MISSED;
          if (payload.duration && payload.endTime) {
            disconnectStatus = CallStatus.END;
          } else if (payload.endedBy === 'USER') {
            // directly remove poup without any delay (INITIATED call declined by another agent)
            await handleDisconnect(CallStatus.END, 'Call rejected by another agent', true);
          }
          await handleDisconnect(disconnectStatus);
        }
      }
    };

    window.addEventListener('message', handleCallDisconnected);
    return () => window.removeEventListener('message', handleCallDisconnected);
  }, [call.callId, call.status]);

  const setupWebRTC = async (sdpOffer: string, reconnect?: boolean) => {
    if (isSettingUpWebRTCRef.current) return;
    isSettingUpWebRTCRef.current = true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      setLocalStream(stream);
      if (localAudioRef.current) {
        localAudioRef.current.srcObject = stream;
      }

      const pc = new RTCPeerConnection(servers);
      setPeerConnection(pc);

      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });

      pc.ontrack = (event) => {
        setRemoteStream(event.streams[0]);
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = event.streams[0];
        }
      };

      pc.onconnectionstatechange = () => {
        switch (pc.connectionState) {
          case 'connecting':
            onAcceptStateChange(call, CallStatus.Connecting);
            break;
          case 'connected':
            onAcceptStateChange(call, CallStatus.IN_PROGRESS);
            break;
        }
      };

      await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: sdpOffer }));

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await answerIncommingCallApi({
        callId: call.callId,
        sdp: answer.sdp || '',
        sdpType: answer.type,
        reconnect: !!reconnect,
      })
        .catch((err) => {
          console.error('Error accepting call:', err);
          setError(err instanceof Error ? err.message : 'Failed to establish call connection');
          stream.getTracks().forEach((track) => {
            track.stop();
            stream.removeTrack(track);
          });
          if (localAudioRef.current) localAudioRef.current.srcObject = null;
          if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
          pc.close();
          cleanUp();
          addNotification({
            type: 'error',
            message: err instanceof Error ? err.message : 'Failed to establish call connection',
          });
        })
        .finally(() => {
          isSettingUpWebRTCRef.current = false;
        });
    } catch (error) {
      console.error('Error in setupWebRTC:', error);
      isSettingUpWebRTCRef.current = false;
      throw error;
    }
  };

  // use useCallBack to memoize the acceptCall function
  const acceptCall = useCallback(async () => {
    try {
      stopRingtone();
      await setupWebRTC(call.sdp, call.reconnect);
    } catch (err) {
      console.error('Error accepting call:', err);
      addNotification({ type: 'error', message: (err as Error).message || 'Failed to accept call' });
    }
  }, [call.sdp, call.reconnect]);

  async function handleRemoteSDP(pc: RTCPeerConnection, localSdp: string, call: Call) {
    if (call.reconnect) {
      const response = await getNewReconnectSDPApi({
        callId: call.callId,
        callOriginType: CallDirection.OUTGOING,
        sdp: localSdp,
      });

      if (response.success && response.data?.sdp) {
        await pc.setRemoteDescription(new RTCSessionDescription({ type: response.data.type, sdp: response.data.sdp }));
      } else {
        throw new Error('Failed to get reconnect SDP');
      }
    } else {
      const response = await createOutgoingCallApi({
        integrationId: call.integrationId!,
        customerNumber: call.customerNumber,
        sdp: localSdp,
        sdpType: 'offer',
      });

      if (response?.success && response?.data?.callId && response?.answer?.sdp) {
        updateOutgoingCallId({
          tempCallId: call.callId,
          newCallId: response.data.callId,
        });
        await pc.setRemoteDescription(
          new RTCSessionDescription({ type: response.answer.type, sdp: response.answer.sdp })
        );
      } else {
        throw new Error('Failed to create outgoing call');
      }
    }
  }

  const startOutgoingCall = useCallback(async () => {
    if (isSettingUpWebRTCRef.current) return;
    isSettingUpWebRTCRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      setLocalStream(stream);
      if (localAudioRef.current) localAudioRef.current.srcObject = stream;

      const pc = new RTCPeerConnection(servers);
      setPeerConnection(pc);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      pc.ontrack = (event) => {
        setRemoteStream(event.streams[0]);
        if (remoteAudioRef.current) remoteAudioRef.current.srcObject = event.streams[0];
      };
      pc.onconnectionstatechange = () => {
        switch (pc.connectionState) {
          case 'connecting':
            onAcceptStateChange(call, CallStatus.Connecting);
            break;
          case 'connected':
            if (call.reconnect) {
              onAcceptStateChange(call, CallStatus.IN_PROGRESS);
            }
            break;
        }
      };

      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);

      await handleRemoteSDP(pc, offer.sdp || '', call);
    } catch (err) {
      const errorMessage = (err as Error).name === 'NotAllowedError' ? 'Mic permission not allowed' : (err as Error).message;
      console.error('Error starting outgoing call:', err);
      if((err as Error).name !== 'InvalidStateError') {
        addNotification({ type: 'error', message: errorMessage || 'Failed to start call' });
      }
      isSettingUpWebRTCRef.current = false;
      cleanUp();
      onDisconnect(CallStatus.FAILED, call);
    }
  }, [servers, call.customerName, call.customerNumber, call.wabaNumber, call.wabaName, call.reconnect]);

  // Automatically start outgoing offer flow when chip is active and in INITIATED OUTGOING state
  useEffect(() => {
    if (
      call.direction === CallDirection.OUTGOING &&
      call.status === CallStatus.INITIATED &&
      !peerConnection &&
      !isSettingUpWebRTCRef.current &&
      !outgoingStartedRef.current &&
      call.shouldConnect
    ) {
      outgoingStartedRef.current = true;
      void startOutgoingCall();
    }
  }, [call.direction, call.status, peerConnection, call.shouldConnect, startOutgoingCall]);

  const handleDisconnect = async (status: CallDisconnectStatus, reason?: string, removeWithoutDelay = false) => {
    try {
      cleanUp();
      await declineCallApi({
        callId: call.callId,
      }).catch((error) => {
        console.error('Error disconnecting call:', error);
      });
      setDisconnectReason(reason || 'Call ended');
      onDisconnect(status, call, removeWithoutDelay);
    } catch (err) {
      console.error('Error during call disconnect:', err);
      setDisconnectReason(reason || 'Call ended');
      onDisconnect(status, call, removeWithoutDelay);
    }
  };

  const toggleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      audioTrack.enabled = !audioTrack.enabled;
      setIsMuted(!audioTrack.enabled);
    }
  };

  const toggleSpeaker = () => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = !remoteAudioRef.current.muted;
      setIsSpeakerOff(remoteAudioRef.current.muted);
    }
  };

  const handleChatClick = () => {
    navigateToConversation({ phoneNumber: call.customerNumber, waba: call.wabaNumber });
  };

  return (
    <Box
      css={css`
        position: relative;
      `}
    >
      {!isActive && (
        <ChipView call={call} onSelect={onSelect} callIndex={callIndex} isAnyCallActive={isAnyCallActive} />
      )}
      {isActive && (
        <ExpandedView
          call={call}
          isMuted={isMuted}
          isSpeakerOff={isSpeakerOff}
          toggleMute={toggleMute}
          toggleSpeaker={toggleSpeaker}
          handleChatClick={handleChatClick}
          onRequestAccept={onRequestAccept}
          onCloseChip={onCloseChip}
          onMinimize={onMinimize}
          handleDisconnect={handleDisconnect}
        />
      )}
      {/* Hidden audio elements are always mounted so minimizing does not break audio */}
      <audio ref={localAudioRef} autoPlay muted style={{ display: 'none' }} />
      <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
    </Box>
  );
};

export default CallChip;
